import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Collapse,
  Tooltip,
  IconButton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { motion } from 'framer-motion';
import Sidebar from './Sidebar';
import DashboardFooter from './DashboardFooter';
import { useDispatch } from 'react-redux';
import { fetchWorkflows } from '../../state/slices/workflowSlice';
import { fetchProjects } from '../../state/slices/projectSlice';
import { fetchConnections } from '../../state/slices/connectionsSlice';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import axiosInstance from '../../apis/axiosInstance';
import OnboardingTour from '../../components/OnboardingTour';
import salesforceImg from '../../workflows/images/salesforce.png';
import oracleImg from '../../workflows/images/oracle.png';
import hubspotImg from '../../workflows/images/hubspot.png';
import sqlImg from '../../workflows/images/sql.png';
import jiraImg from '../../workflows/images/JIRA.svg';
import iconDash1 from '../assets/images/icondash1.png';
import iconDash2 from '../assets/images/icondash2.png';
import iconDash3 from '../assets/images/icondash3.png';
import iconDash4 from '../assets/images/icondash4.png';
import dashIcon1 from '../assets/images/dashIcon1.png';
import dashIcon2 from '../assets/images/dashIcon2.png';
import dashIcon3 from '../assets/images/dashIcon3.png';
import dashIcon4 from '../assets/images/dashIcon4.png';
import plugIcon from '../assets/images/plugIcon.png';
import addPlusIcon from '../assets/images/Add_Plus.png';

export const DashboardLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [expandedCards, setExpandedCards] = React.useState({});

  const toggleCard = (cardTitle) => {
    setExpandedCards((prev) => ({
      ...prev,
      [cardTitle]: !prev[cardTitle],
    }));
  };



  useEffect(() => {
    dispatch(fetchProjects());
    dispatch(fetchConnections());
    dispatch(fetchWorkflows());
  }, [dispatch]);

  useEffect(() => {
    const refreshTrialStatus = async () => {
      try {
        const token = Cookies.get('authToken');
        if (!token) return;

        const response = await axiosInstance.get('/auth/user', {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.trial_status) {
          Cookies.set('trialStatus', JSON.stringify(response.data.trial_status), { 
            expires: 1, 
            secure: true 
          });
        }
      } catch (error) {
        console.error('Failed to refresh trial status:', error);
      }
    };

    refreshTrialStatus();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  const cardHoverVariants = {
    hover: {
      scale: 1.03,
      y: -2,
      transition: {
        duration: 0.2,
        ease: 'easeInOut',
      },
    },
  };

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" style={{ width: '100%', overflowX: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'hidden',
          paddingTop: '0px',
        }}
      >
        <Sidebar />

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <Box
          sx={{
            paddingTop: { xs: '0.5rem', sm: '0.75rem', md: '1rem' },
            paddingRight: { xs: '1rem', sm: '2rem', md: '3rem' },
            paddingLeft: { xs: '1rem', sm: '1.5rem', md: '2rem' },
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            overflowY: 'auto',
            background: '#FAFBFD',
            boxSizing: 'border-box',
            flex: 1,
            minWidth: 0,
          }}
        >

          <motion.div variants={itemVariants}>
            <Box sx={{ mb: { xs: 3, sm: 4 }, mt: { xs: 1, sm: 2 } }}>
              <Typography
                sx={{
                  fontSize: { xs: '1.125rem', sm: '1.25rem' },
                  fontWeight: 700,
                  fontStyle: 'normal',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  verticalAlign: 'middle',
                  color: '#383838',
                  mb: 1,
                  fontFamily: 'IBM Plex Sans, sans-serif',
                }}
              >
                Choose a service to get started?
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  fontWeight: 400,
                  fontStyle: 'normal',
                  lineHeight: '1.5rem',
                  letterSpacing: '-0.31px',
                  color: '#4A5565',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                Start with a service based on what you want to achieve with your data.
              </Typography>
            </Box>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: { xs: 5, sm: 5, md: 8 } }} data-tour="dashboard-cards">
              {[
                {
                  title: 'Build Data Pipelines',
                  description: 'Move data from one system to another automatically.',
                  useCase: 'Salesforce to Database',
                  useCases: ['Salesforce to Database', 'Stripe to Analytics', 'HubSpot to Datawarehouse'],
                  buttonText: 'Create Pipeline',
                  icon: iconDash1,
                  iconColor: '#FFA726',
                  bgColor: '#FFF3E0',
                  path: '/createpipeline',
                },
                {
                  title: 'Monitor Pipeline Runs',
                  description: 'Check if your data is flowing correctly.',
                  useCase: 'See if pipelines succeeded or failed',
                  useCases: ['See if pipelines succeeded or failed', 'Debug errors', 'Track execution history'],
                  buttonText: 'View Runs',
                  icon: iconDash2,
                  iconColor: '#42A5F5',
                  bgColor: '#E3F2FD',
                  path: '/reports',
                },
                {
                  title: 'Manage Data Connections',
                  description: 'Securely connect your apps and databases once, reuse everywhere.',
                  useCase: 'Connect Salesforce, HubSpot, Stripe',
                  useCases: ['Connect Salesforce, HubSpot, Stripe', 'Connect databases like Postgres', 'Manage credentials safely'],
                  buttonText: 'View Connections',
                  icon: iconDash3,
                  iconColor: '#66BB6A',
                  bgColor: '#E8F5E9',
                  path: '/myconnections',
                },
                {
                  title: 'Pre-Made Templates',
                  description: 'Start faster using pre-built pipeline patterns.',
                  useCase: 'SaaS -> Analytics starter pipeline',
                  useCases: ['SaaS → Analytics starter pipeline', 'Standard transformation logic', 'Reusable data flows'],
                  buttonText: 'Browse Templates',
                  icon: iconDash4,
                  iconColor: '#AB47BC',
                  bgColor: '#F3E5F5',
                  path: '/pre-made-templates',
                },
              ].map((card, index) => (
                <Grid item xs={12} sm={6} md={6} lg={3} key={card.title} sx={{ display: 'flex', mb: { xs: 3, sm: 3, md: 0 } }}>
                  <motion.div
                    variants={cardHoverVariants}
                    whileHover="hover"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.4 }}
                    style={{ width: '100%', display: 'flex' }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: '4px',
                        p: { xs: 1.25, sm: 1.5 },
                        border: '1px solid #E6E6E6',
                        background: card.title === 'Build Data Pipelines'
                          ? 'linear-gradient(137.59deg, #FEFEED 0%, rgba(254, 254, 237, 0) 102.06%)'
                          : card.title === 'Monitor Pipeline Runs'
                          ? 'linear-gradient(135.42deg, #EEF3FF -0.05%, rgba(238, 243, 255, 0) 101.36%)'
                          : card.title === 'Manage Data Connections'
                          ? 'linear-gradient(135.68deg, #E7FAF4 0.05%, rgba(231, 250, 244, 0) 98.27%)'
                          : 'linear-gradient(133.84deg, #F7EDFF 0%, rgba(247, 237, 255, 0) 101.42%)',
                        boxShadow: '0px 0px 6px 0px #7878781A',
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0px 0px 6px 0px #7878781A',
                        },
                      }}
                      onClick={() => card.path && navigate(card.path)}
                    >
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          mb: 1,
                        }}
                      >
                        <Box
                          component="img"
                          src={card.icon}
                          alt={card.title}
                          sx={{
                            width: 40,
                            height: 40,
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          fontWeight: 600,
                          lineHeight: '100%',
                          letterSpacing: '0%',
                          color: '#000000',
                          mb: 0.75,
                          fontFamily: 'IBM Plex Sans, sans-serif',
                        }}
                      >
                        {card.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                          fontWeight: 400,
                          lineHeight: '140%',
                          letterSpacing: '0%',
                          color: '#525252',
                          mb: { xs: 1.5, sm: 2 },
                          fontFamily: 'Inter, sans-serif',
                        }}
                      >
                        {card.description}
                      </Typography>
                      <Box sx={{ mb: 1.5 }}>
                        <Typography
                          sx={{
                            fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                            fontWeight: 600,
                            lineHeight: '100%',
                            letterSpacing: '0%',
                            color: '#000000',
                            mb: 1,
                            fontFamily: 'IBM Plex Sans, sans-serif',
                          }}
                        >
                          Use Case :
                        </Typography>
                        <Box
                          component="ul"
                          sx={{
                            margin: 0,
                            paddingLeft: '1.25rem',
                            listStyleType: 'disc',
                          }}
                        >
                          {(card.useCases || [card.useCase]).map((useCase, idx) => (
                            <Box
                              component="li"
                              key={idx}
                              sx={{
                                fontSize: '0.8125rem',
                                fontWeight: 400,
                                lineHeight: '140%',
                                letterSpacing: '0%',
                                color: '#525252',
                                fontFamily: 'Inter, sans-serif',
                                mb: 0.25,
                                '&:last-child': {
                                  mb: 0,
                                },
                              }}
                            >
                              {useCase}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                      <Button
                        variant={card.title === 'Build Data Pipelines' ? 'contained' : 'outlined'}
                        sx={{
                          textTransform: 'none',
                          borderRadius: '100px',
                          px: { xs: 1.5, sm: 2 },
                          py: { xs: 1.25, sm: 1.5 },
                          fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                          fontWeight: 600,
                          fontStyle: 'normal',
                          lineHeight: '100%',
                          letterSpacing: '0%',
                          textAlign: 'right',
                          color: card.title === 'Build Data Pipelines' ? '#FFFFFF' : '#0F172A',
                          fontFamily: 'Inter, sans-serif',
                          backgroundColor: card.title === 'Build Data Pipelines' ? '#0F172A' : 'transparent',
                          borderColor: card.title === 'Build Data Pipelines' ? 'transparent' : '#0F172A',
                          alignSelf: 'flex-start',
                          mt: 'auto',
                          '&:hover': {
                            backgroundColor: card.title === 'Build Data Pipelines' ? '#1E293B' : 'rgba(15, 23, 42, 0.1)',
                            borderColor: card.title === 'Build Data Pipelines' ? 'transparent' : '#0F172A',
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (card.path) navigate(card.path);
                        }}
                      >
                        {card.buttonText}
                      </Button>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Typography
              sx={{
                fontSize: { xs: '1.125rem', sm: '1.25rem' },
                fontWeight: 700,
                fontStyle: 'normal',
                lineHeight: '100%',
                letterSpacing: '0%',
                verticalAlign: 'middle',
                color: '#383838',
                mb: { xs: 2, sm: 3 },
                fontFamily: 'IBM Plex Sans, sans-serif',
              }}
            >
              Data sources you can connect
            </Typography>
            <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: { xs: 3, sm: 4, md: 5 } }} data-tour="data-sources">
              {[
                {
                  title: 'SaaS API Connectors',
                  description: 'Pull data from popular cloud tools without writing code.',
                  viewCount: '04',
                  icon: dashIcon1,
                  iconColor: '#42A5F5',
                  bgColor: '#EEF4FF',
                  tooltip: 'Connect to cloud-based software applications via their APIs. Pull data from popular SaaS tools like Salesforce, HubSpot, Jira, and more without writing custom code. Ideal for integrating CRM, marketing, and collaboration platforms.',
                },
                {
                  title: 'Database Connectors',
                  description: 'Move or sync data between databases.',
                  viewCount: '02',
                  icon: dashIcon2,
                  iconColor: '#FFA726',
                  bgColor: '#FEFEE2',
                  tooltip: 'Connect to relational and non-relational databases to move, sync, or query data. Supports databases like SQL Server, Oracle, MySQL, PostgreSQL. Perfect for data migration, replication, and ETL operations between database systems.',
                },
                {
                  title: 'Streaming Connectors',
                  description: 'Ingest live data as it happens.',
                  viewCount: '03',
                  icon: dashIcon3,
                  iconColor: '#66BB6A',
                  bgColor: '#E9FAF5',
                  tooltip: 'Connect to real-time data streams and event sources. Ingest live data as it happens from message queues, event streams, and real-time APIs. Ideal for real-time analytics, monitoring, and event-driven architectures.',
                },
                {
                  title: 'File/Storage Connectors',
                  description: 'Bring data from files and storage.',
                  viewCount: '03',
                  icon: dashIcon4,
                  iconColor: '#AB47BC',
                  bgColor: '#F7EDFF',
                  tooltip: 'Connect to file storage systems and cloud storage services. Import data from files (CSV, JSON, Excel) and storage platforms like S3, Azure Blob, Google Cloud Storage. Perfect for batch data processing and file-based integrations.',
                },
              ].map((card, index) => (
                <Grid item xs={12} sm={6} md={6} lg={3} key={card.title}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + index * 0.1, duration: 0.4 }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: '4px',
                        p: { xs: 1.5, sm: 2 },
                        border: '0.5px solid #E6E6E6',
                        background: '#FFFFFF',
                        boxShadow: '0px 0px 6px 0px #7878781A',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0px 0px 6px 0px #7878781A',
                        },
                      }}
                      onClick={() => {}}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 1.5,
                          pb: 1.5,
                          borderBottom: '1px solid #E6E6E6',
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Typography
                              sx={{
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                fontWeight: 500,
                                fontStyle: 'normal',
                                lineHeight: '100%',
                                letterSpacing: '0%',
                                color: '#000000',
                                fontFamily: 'IBM Plex Sans, sans-serif',
                              }}
                            >
                              {card.title}
                            </Typography>
                            <Tooltip
                              title={card.tooltip}
                              arrow
                              placement="top"
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    maxWidth: 400,
                                    fontFamily: 'IBM Plex Sans, sans-serif',
                                    fontSize: '0.8125rem',
                                    lineHeight: 1.5,
                                    padding: '0.75rem 1rem',
                                  },
                                },
                              }}
                            >
                              <IconButton
                                size="small"
                                sx={{
                                  padding: 0.25,
                                  color: '#6B7280',
                                  '&:hover': {
                                    color: '#2F5D63',
                                    backgroundColor: 'transparent',
                                  },
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                <InfoOutlinedIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Typography
                            sx={{
                              fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                              fontWeight: 400,
                              fontStyle: 'normal',
                              lineHeight: '140%',
                              letterSpacing: '0%',
                              color: '#525252',
                              fontFamily: 'Inter, sans-serif',
                            }}
                          >
                            {card.description}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '8px',
                            background: card.bgColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            ml: 1,
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            component="img"
                            src={card.icon}
                            alt={card.title}
                            sx={{
                              width: 32,
                              height: 32,
                              objectFit: 'contain',
                            }}
                          />
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          component="img"
                          src={plugIcon}
                          alt="Plug"
                          sx={{
                            width: 16,
                            height: 16,
                            objectFit: 'contain',
                            mr: 0.75,
                          }}
                        />
                        <Button
                          variant="contained"
                          size="small"
                          sx={{
                            textTransform: 'none',
                            borderRadius: '42px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            fontStyle: 'normal',
                            lineHeight: '140%',
                            letterSpacing: '0%',
                            color: '#FFFFFF',
                            backgroundColor: '#3E9663',
                            fontFamily: 'Inter, sans-serif',
                            px: 1.5,
                            py: 0.5,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCard(card.title);
                          }}
                        >
                          View ({card.viewCount})
                        </Button>
                        <Box
                          sx={{
                            width: '1px',
                            height: '20px',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            mx: 1.5,
                            flexShrink: 0,
                          }}
                        />
                        <Box
                          component="img"
                          src={addPlusIcon}
                          alt="Add"
                          sx={{
                            width: 16,
                            height: 16,
                            objectFit: 'contain',
                            mr: 0.75,
                          }}
                        />
                        <Button
                          variant="contained"
                          size="small"
                          sx={{
                            textTransform: 'none',
                            borderRadius: '42px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            fontStyle: 'normal',
                            lineHeight: '140%',
                            letterSpacing: '0%',
                            color: '#FFFFFF',
                            backgroundColor: '#3D3D3D',
                            fontFamily: 'Inter, sans-serif',
                            px: 1.5,
                            py: 0.5,
                          }}
                        >
                          Connection
                        </Button>
                      </Box>
                      <Collapse in={expandedCards[card.title] || false}>
                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #E6E6E6' }}>
                          <Typography
                            sx={{
                              fontFamily: 'IBM Plex Sans, sans-serif',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              color: '#0F172A',
                              mb: 1.5,
                            }}
                          >
                            Connectors
                          </Typography>
                          <Grid container spacing={1.5}>
                            {[
                              { name: 'Salesforce', img: salesforceImg },
                              { name: 'Oracle', img: oracleImg },
                              { name: 'HubSpot', img: hubspotImg },
                              { name: 'SQL', img: sqlImg },
                              { name: 'Jira', img: jiraImg },
                            ].map((conn) => (
                              <Grid item xs={12} sm={6} key={conn.name}>
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 1.5,
                                    borderRadius: '8px',
                                    border: '1px solid #E6E6E6',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.25,
                                    cursor: 'pointer',
                                    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                                    '&:hover': {
                                      boxShadow: '0px 6px 16px rgba(0,0,0,0.12)',
                                      transform: 'translateY(-2px)',
                                    },
                                  }}
                                >
                                  <Box component="img" src={conn.img} alt={conn.name} sx={{ width: 20, height: 20 }} />
                                  <Typography
                                    sx={{
                                      fontFamily: 'Inter, sans-serif',
                                      color: '#222222',
                                      fontSize: '0.8125rem',
                                      fontWeight: 500,
                                    }}
                                  >
                                    {conn.name}
                                  </Typography>
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      </Collapse>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
          </Box>
          <Box sx={{ width: '100%' }}>
            <DashboardFooter />
          </Box>
        </Box>
      </Box>
      <OnboardingTour />
    </motion.div>
  );
};

export default DashboardLayout;
